'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const profiles = [
  {
    icon: 'BuildingOfficeIcon',
    title: 'Conciergeries indépendantes',
    desc: 'Structures de 1 à 5 personnes gérant 5 à 150 logements en location saisonnière, sur 1 à 5 villes.',
    fit: true,
  },
  {
    icon: 'HomeModernIcon',
    title: 'Gestionnaires multi-propriétés',
    desc: 'Investisseurs ou professionnels gérant plusieurs logements en direct, en quête d\'automatisation.',
    fit: true,
  },
  {
    icon: 'ChartBarIcon',
    title: 'Structures en croissance',
    desc: 'Conciergeries cherchant à structurer leur gestion pour passer de 5 à 30+ biens sans recruter.',
    fit: true,
  },
  {
    icon: 'XCircleIcon',
    title: 'Particuliers avec 1 logement',
    desc: 'Ce programme cible les professionnels gérant plusieurs biens. Les particuliers ne correspondent pas au profil.',
    fit: false,
  },
];

const expectations = [
  {
    icon: 'CheckIcon',
    text: 'Utilisation réelle de GiteZen sur au moins 1 logement actif pendant 3 mois',
  },
  {
    icon: 'ChatBubbleLeftRightIcon',
    text: 'Retour d\'expérience structuré (formulaire ou entretien de 30 min)',
  },
  {
    icon: 'VideoCameraIcon',
    text: 'Témoignage écrit ou vidéo sur votre expérience avec GiteZen',
  },
  {
    icon: 'LinkIcon',
    text: 'Lien contextualisé vers GiteZen sur votre site partenaire',
  },
];

export default function AudienceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-el').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 px-6 border-t border-white/[0.05]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* À qui s'adresse */}
          <div>
            <div className="reveal-el mb-10">
              <p className="text-moss-light font-bold uppercase tracking-[0.25em] text-xs mb-4">À qui s'adresse ce programme</p>
              <h2 className="font-fraunces text-4xl md:text-5xl font-light italic leading-tight text-cream">
                Fait pour les<br />
                <span className="text-moss-light">vrais professionnels.</span>
              </h2>
            </div>

            <div className="space-y-3">
              {profiles.map((profile, i) => (
                <div
                  key={i}
                  className={`reveal-el glass-card rounded-xl p-5 flex items-start gap-4 spotlight-card ${
                    !profile.fit ? 'opacity-50' : ''
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${
                      profile.fit
                        ? 'bg-moss/15 border border-moss/30' :'bg-white/[0.03] border border-white/10'
                    }`}
                  >
                    <Icon
                      name={profile.icon as any}
                      size={18}
                      className={profile.fit ? 'text-moss-light' : 'text-cream/30'}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-cream font-semibold text-sm">{profile.title}</h4>
                      {!profile.fit && (
                        <span className="text-[9px] uppercase tracking-widest text-cream/30 border border-white/10 px-2 py-0.5 rounded-full">
                          Non éligible
                        </span>
                      )}
                    </div>
                    <p className="text-cream/50 text-xs leading-relaxed">{profile.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ce que nous attendons */}
          <div>
            <div className="reveal-el mb-10">
              <p className="text-gold font-bold uppercase tracking-[0.25em] text-xs mb-4">Ce que nous attendons</p>
              <h2 className="font-fraunces text-4xl md:text-5xl font-light italic leading-tight text-cream">
                Un partenariat<br />
                <span className="text-gold">réel et concret.</span>
              </h2>
            </div>

            <p className="text-cream/60 leading-relaxed mb-8 reveal-el">
              Ce n'est pas un accès gratuit sans contrepartie. Nous cherchons des partenaires engagés qui utilisent vraiment l'outil et nous aident à le perfectionner. En échange, vous recevez une valeur significative.
            </p>

            <div className="space-y-4">
              {expectations.map((exp, i) => (
                <div key={i} className="reveal-el flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <Icon name={exp.icon as any} size={16} className="text-gold" />
                  </div>
                  <p className="text-cream/70 text-sm leading-relaxed pt-1">{exp.text}</p>
                </div>
              ))}
            </div>

            {/* Reassurance */}
            <div className="mt-8 glass-card rounded-xl p-5 border-l-2 border-moss reveal-el">
              <p className="text-cream/80 text-sm leading-relaxed">
                <strong className="text-cream">Aucune obligation de rester.</strong> Si GiteZen ne correspond pas à vos besoins après les 3 mois, vous n'avez aucun engagement. Nous préférons des partenaires honnêtes à des partenaires captifs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}